import React, { useEffect, useState } from 'react';
import { Button, Grid, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { authApiClient } from '../../../api/client/axios';
import { showSnackbar } from '../../../store/snackbarSlice';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import appConfig from '../../../config/appConfig';

const UpdatePasswordValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  new_password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[@$!%*?&]/, 'Password must contain at least one special character (@, $, !, %, *, ?, &)')
    .required('Password is required'),
  confirmPassword: Yup.string().oneOf([Yup.ref('new_password'), null], 'Passwords must match'),
  otp: Yup.string()
    .required('OTP is required')
    .matches(/^\d{6}$/, 'OTP must be exactly 6 digits')
    .length(6, 'OTP must be exactly 6 digits'),
});

const ForgotPasswordUpdateForm = ({ email }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timer, setTimer] = useState(120);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    setError,
  } = useForm({
    resolver: yupResolver(UpdatePasswordValidationSchema),
    defaultValues: {
      email: email,
      termsAndCondition: false,
    },
  });

  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [timer]);

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .post('/reset-password', data)
      .then((res) => {
        navigate('/login', { replace: true });
        dispatch(
          showSnackbar({
            message: 'Password updated successfully!.',
            severity: 'success',
          }),
        );
      })
      .catch((err) => {
        if (err?.response?.data?.errors?.new_password) {
          setError('new_password', { message: err?.response?.data?.errors?.new_password });
        } else {
          let message =
            Object.values(err?.response?.data?.errors ?? [])
              .flat()
              .join('\n') ||
            err.message ||
            'Something went wrong';
          dispatch(showSnackbar({ message, severity: 'error' }));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResendOtp = () => {
    authApiClient
      .post('/forgot-password', { email: email })
      .then((res) => {
        dispatch(showSnackbar({ message: 'OTP sent successfully', severity: 'success' }));
        setTimer(120);
      })
      .catch((err) => {
        dispatch(
          showSnackbar({
            message: err?.response?.data?.errors?.message ?? 'Error sending OTP. Please try again.',
            severity: 'error',
          }),
        );
      });
  };

  const formData = watch();
  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt={`${appConfig.brandName} logo`} width="50%" />
      </Stack>

      <Stack spacing={2}>
        <CustomTextInput name="email" label="Email" control={control} errors={errors} disabled={true} />
        <CustomTextInput
          name="otp"
          label="OTP"
          control={control}
          errors={errors}
          placeholder="Enter 6-digit OTP"
          inputProps={{ maxLength: 6 }}
        />
        <Grid container spacing={2} sx={{ justifyContent: 'right' }}>
          {timer > 0 ? (
            <Typography variant="body2" color="textSecondary" align="right">
              Resend OTP in {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
            </Typography>
          ) : (
            <Button onClick={handleResendOtp} variant="text" color="primary">
              Resend OTP
            </Button>
          )}
        </Grid>
        <CustomTextInput
          name="new_password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <CustomTextInput
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          label="Confirm Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Grid container spacing={0}>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: formData.new_password?.length >= 8 ? 'green' : '#ff0000' }}>
              Minimum 8 characters.
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[A-Z]/.test(formData.new_password) ? 'green' : '#ff0000' }}>
              At least 1 uppercase letter.
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[0-9]/.test(formData.new_password) ? 'green' : '#ff0000' }}>
              At least 1 number.
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography
              variant="caption"
              sx={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(formData.new_password) ? 'green' : '#ff0000' }}
            >
              At least one special character.
            </Typography>
          </Grid>
        </Grid>
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(onSubmit)}>
          {loading ? 'Updating...' : 'Update Password'}
        </Button>
        <Typography variant="body2">
          <Link to="/login" style={{ textDecoration: 'none' }}>
            Back to Login
          </Link>
        </Typography>
      </Stack>
    </>
  );
};

export default ForgotPasswordUpdateForm;
